import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckIcon, PlusIcon, TrashIcon, PencilSquareIcon, BoltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tip } from '../ui/tip';
import { confirmDialog } from '../ConfirmDialog';
import { Avatar } from './TaskRow';
import type { Assignee, Routine, RoutineInstance, RoutineItem } from './types';

const SCHEDULES: Array<{ value: Routine['schedule']; label: string }> = [
  { value: 'daily', label: 'Todos los días' },
  { value: 'workdays', label: 'Lunes a sábado' },
  { value: 'weekdays', label: 'Lunes a viernes' },
  { value: 'weekly', label: 'Un día por semana' },
  { value: 'monthly', label: 'Un día por mes' }
];
const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const AUTO_CHECKS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Lo tilda una persona' },
  { value: 'cash_closure', label: 'Solo: cuando se cierra la caja del día' },
  { value: 'remitos_no_draft', label: 'Solo: cuando no queda ningún remito en borrador' }
];

const horaLabel = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

type Borrador = {
  name: string; area: string; schedule: Routine['schedule']; scheduleDay: number; startTime: string; cutoffTime: string;
  notifyUserId: string; isActive: boolean; items: RoutineItem[];
};

const vacio = (currentUserId: string): Borrador => ({
  name: '', area: '', schedule: 'workdays', scheduleDay: 1, startTime: '08:00', cutoffTime: '', notifyUserId: currentUserId, isActive: true,
  items: [{ title: '', assignedTo: '', autoCheck: null }]
});

const desdeRutina = (r: Routine): Borrador => ({
  name: r.name, area: r.area || '', schedule: r.schedule, scheduleDay: r.scheduleDay ?? 1, startTime: r.startTime || '08:00',
  cutoffTime: r.cutoffTime || '', notifyUserId: r.notifyUserId || '', isActive: r.isActive,
  items: r.items.map((i) => ({ id: i.id, title: i.title, note: i.note || '', assignedTo: i.assignedTo || '', autoCheck: i.autoCheck || null }))
});

const RoutineEditor: React.FC<{
  open: boolean; onClose: () => void; routine: Routine | null; assignees: Assignee[]; currentUserId: string; onSaved: () => void;
}> = ({ open, onClose, routine, assignees, currentUserId, onSaved }) => {
  const [b, setB] = useState<Borrador>(() => (routine ? desdeRutina(routine) : vacio(currentUserId)));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setB(routine ? desdeRutina(routine) : vacio(currentUserId)); }, [open, routine, currentUserId]);

  const setItem = (idx: number, patch: Partial<RoutineItem>) => setB((p) => ({ ...p, items: p.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const quitarItem = (idx: number) => setB((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  const agregarItem = () => setB((p) => ({ ...p, items: [...p.items, { title: '', assignedTo: '', autoCheck: null }] }));

  const guardar = async () => {
    const items = b.items.filter((i) => i.title.trim()).map((i, idx) => ({ ...i, assignedTo: i.assignedTo || null, autoCheck: i.autoCheck || null, sortOrder: idx }));
    if (!b.name.trim()) { toast.error('Poné un nombre'); return; }
    if (!items.length) { toast.error('Cargá al menos un punto'); return; }
    if (!/^\d{1,2}:\d{2}$/.test(b.startTime)) { toast.error('Hora de inicio en formato HH:MM'); return; }
    if (b.cutoffTime && !/^\d{1,2}:\d{2}$/.test(b.cutoffTime)) { toast.error('Hora de corte en formato HH:MM'); return; }
    setSaving(true);
    try {
      const payload = { ...b, area: b.area || null, cutoffTime: b.cutoffTime || null, notifyUserId: b.notifyUserId || null, scheduleDay: b.schedule === 'weekly' || b.schedule === 'monthly' ? b.scheduleDay : null, items };
      if (routine) await axios.put(`/api/tasks/routines/${routine.id}`, payload);
      else await axios.post('/api/tasks/routines', payload);
      toast.success(routine ? 'Rutina guardada' : 'Rutina creada');
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar la rutina');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{routine ? `Editar rutina: ${routine.name}` : 'Nueva rutina'}</DialogTitle>
          <DialogDescription>Un checklist que se repite. Cada día que aplica, sus puntos aparecen como tareas y queda registrado quién tildó cada uno.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label>Nombre</Label>
            <Input value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} placeholder="Apertura, Cierre, Semanal…" className="h-9" />
          </div>
          <div className="space-y-1">
            <Label>Área</Label>
            <Input value={b.area} onChange={(e) => setB({ ...b, area: e.target.value })} placeholder="Taller y lavadero" className="h-9" />
          </div>
          <div className="space-y-1">
            <Label>Se repite</Label>
            <Select value={b.schedule} onValueChange={(v) => setB({ ...b, schedule: v as Routine['schedule'] })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {b.schedule === 'weekly' && (
            <div className="space-y-1">
              <Label>Qué día</Label>
              <Select value={String(b.scheduleDay)} onValueChange={(v) => setB({ ...b, scheduleDay: Number(v) })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5, 6, 0].map((d) => <SelectItem key={d} value={String(d)}>{DIAS[d]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {b.schedule === 'monthly' && (
            <div className="space-y-1">
              <Label>Día del mes</Label>
              <Input type="number" min={1} max={31} value={b.scheduleDay} onChange={(e) => setB({ ...b, scheduleDay: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} className="h-9" />
            </div>
          )}
          <div className="space-y-1">
            <Label>Aparece a las</Label>
            <Input value={b.startTime} onChange={(e) => setB({ ...b, startTime: e.target.value })} placeholder="08:00" className="h-9 w-28" />
          </div>
          <div className="space-y-1">
            <Label>Hora de corte (avisa si falta algo)</Label>
            <Input value={b.cutoffTime} onChange={(e) => setB({ ...b, cutoffTime: e.target.value })} placeholder="18:30 · vacío = no avisa" className="h-9 w-40" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Avisar a</Label>
            <Select value={b.notifyUserId || '__none__'} onValueChange={(v) => setB({ ...b, notifyUserId: v === '__none__' ? '' : v })}>
              <SelectTrigger className="h-9 sm:w-72"><SelectValue placeholder="Nadie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nadie</SelectItem>
                {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-2">
          <div className="mb-1.5 flex items-center justify-between">
            <Label>Puntos del checklist</Label>
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={agregarItem}><PlusIcon className="mr-1 h-4 w-4" />Agregar punto</Button>
          </div>
          <div className="space-y-2">
            {b.items.map((it, idx) => (
              <div key={it.id || idx} className="grid grid-cols-1 gap-2 rounded-md border border-border p-2 sm:grid-cols-[minmax(0,1fr)_170px_auto]">
                <div className="space-y-1.5">
                  <Input value={it.title} onChange={(e) => setItem(idx, { title: e.target.value })} placeholder={`Punto ${idx + 1}`} className="h-9" />
                  <Input value={it.note || ''} onChange={(e) => setItem(idx, { note: e.target.value })} placeholder="Nota (opcional)" className="h-8 text-xs" />
                  <Select value={it.autoCheck || '__none__'} onValueChange={(v) => setItem(idx, { autoCheck: (v === '__none__' ? null : v) as RoutineItem['autoCheck'] })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Lo tilda una persona" /></SelectTrigger>
                    <SelectContent>{AUTO_CHECKS.map((a) => <SelectItem key={a.value || 'none'} value={a.value || '__none__'}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Select value={it.assignedTo || '__none__'} onValueChange={(v) => setItem(idx, { assignedTo: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Responsable" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Quien avisa</SelectItem>
                    {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Tip label="Quitar punto">
                  <button type="button" onClick={() => quitarItem(idx)} className="self-start rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Quitar punto">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </Tip>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={b.isActive} onCheckedChange={(v) => setB({ ...b, isActive: v })} />
            <span>{b.isActive ? 'Activa' : 'Inactiva (no genera tareas)'}</span>
          </label>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="button" onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  assignees: Assignee[];
  canManage: boolean;
  currentUserId: string;
  onChanged?: () => void;
}

const RoutinesView: React.FC<Props> = ({ assignees, canManage, currentUserId, onChanged }) => {
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<{ open: boolean; routine: Routine | null }>({ open: false, routine: null });

  const cargar = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/tasks/routines');
      setRoutines(res.data?.routines || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron cargar las rutinas');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const tildar = async (r: Routine, inst: RoutineInstance) => {
    const done = inst.status === 'completed';
    setRoutines((prev) => prev.map((x) => x.id !== r.id || !x.hoy ? x : ({
      ...x,
      hoy: { ...x.hoy, tasks: x.hoy.tasks.map((t) => (t.id === inst.id ? { ...t, status: done ? 'pending' : 'completed', completerName: done ? null : 'vos', completedAt: done ? null : new Date().toISOString() } : t)) }
    })));
    try {
      await axios.put(`/api/tasks/${inst.id}`, { status: done ? 'pending' : 'completed' });
      cargar(true);
      onChanged?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar');
      cargar(true);
    }
  };

  const generar = async (r: Routine) => {
    try {
      const res = await axios.post(`/api/tasks/routines/${r.id}/generate`);
      toast.success(res.data?.created ? `${res.data.created} punto(s) generados` : 'Ya estaba generada');
      cargar(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo generar');
    }
  };

  const activar = async (r: Routine, isActive: boolean) => {
    try {
      await axios.put(`/api/tasks/routines/${r.id}`, { isActive });
      toast.success(isActive ? `${r.name} activada` : `${r.name} pausada`);
      cargar(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cambiar');
    }
  };

  const eliminar = async (r: Routine) => {
    if (!(await confirmDialog({ title: `Eliminar la rutina ${r.name}`, description: 'Se borran sus puntos. Lo ya tildado queda como historial.', confirmText: 'Eliminar' }))) return;
    try {
      await axios.delete(`/api/tasks/routines/${r.id}`);
      toast.success('Rutina eliminada');
      cargar(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-[62ch] text-sm text-muted-foreground">
          Checklists que se repiten solos. Cada día que aplican, sus puntos aparecen con la persona a cargo y queda registrado quién tildó y a qué hora. Si a la hora de corte falta algo, avisa por WhatsApp y en el sistema.
        </p>
        {canManage && (
          <Button type="button" onClick={() => setEditor({ open: true, routine: null })} className="h-9">
            <PlusIcon className="mr-1 h-4 w-4" />Nueva rutina
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Cargando rutinas…</div>
      ) : routines.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Todavía no hay rutinas. Creá la primera: por ejemplo Apertura, Cierre o Semanal.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {routines.map((r) => {
            const hoy = r.hoy;
            const generada = !!hoy && hoy.tasks.length > 0;
            return (
              <div key={r.id} className={`rounded-lg border border-border bg-card ${r.isActive ? '' : 'opacity-70'}`}>
                <div className="flex items-start gap-3 border-b border-border px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{r.name}</span>
                      {!r.isActive && <span className="rounded-full bg-muted px-2 py-px text-[11px] text-muted-foreground">pausada</span>}
                      {r.isActive && !r.aplicaHoy && <span className="rounded-full bg-muted px-2 py-px text-[11px] text-muted-foreground">hoy no aplica</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[r.scheduleLabel, r.startTime ? `desde las ${r.startTime}` : null, r.area].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {generada && <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{hoy!.hechas}/{hoy!.total}</span>}
                  {canManage && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Tip label={r.isActive ? 'Pausar' : 'Activar'}>
                        <span className="inline-flex"><Switch checked={r.isActive} onCheckedChange={(v) => activar(r, v)} /></span>
                      </Tip>
                      <Tip label="Editar"><button type="button" onClick={() => setEditor({ open: true, routine: r })} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Editar"><PencilSquareIcon className="h-4 w-4" /></button></Tip>
                      <Tip label="Eliminar"><button type="button" onClick={() => eliminar(r)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive" aria-label="Eliminar"><TrashIcon className="h-4 w-4" /></button></Tip>
                    </div>
                  )}
                </div>
                {generada && <Progress value={hoy!.pct} className="h-1.5 rounded-none" />}

                {generada ? (
                  <div>
                    {hoy!.tasks.map((t) => {
                      const done = t.status === 'completed';
                      return (
                        <div key={t.id} className="flex items-center gap-2.5 border-t border-border px-4 py-2 first:border-t-0">
                          <button
                            type="button"
                            onClick={() => tildar(r, t)}
                            aria-label={done ? 'Reabrir' : 'Tildar'}
                            className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border ${done ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/70 bg-background hover:border-foreground'}`}
                          >
                            {done && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className={`truncate text-sm ${done ? 'text-muted-foreground line-through' : ''}`}>{t.title}</div>
                            {t.autoCheck && !done && <div className="text-[11px] text-muted-foreground">se tilda solo desde el sistema</div>}
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5 text-xs">
                            {done ? (
                              <span className="text-emerald-600">{[t.completerName, horaLabel(t.completedAt)].filter(Boolean).join(' · ')}</span>
                            ) : (
                              <span className="text-muted-foreground">{(t.assigneeName || '').split(' ')[0]}</span>
                            )}
                            <Avatar id={t.assignedTo} name={t.assigneeName} size="sm" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-3">
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {r.items.map((i) => (
                        <li key={i.id} className="flex items-center gap-2">
                          <span className="h-[14px] w-[14px] shrink-0 rounded border border-muted-foreground/40" />
                          <span className="truncate">{i.title}</span>
                          {i.assigneeName && <span className="ml-auto shrink-0 text-xs">{i.assigneeName.split(' ')[0]}</span>}
                        </li>
                      ))}
                    </ul>
                    {r.isActive && r.aplicaHoy && (
                      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>Los puntos de hoy aparecen a las {r.startTime || '08:00'}.</span>
                        {canManage && (
                          <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => generar(r)}><BoltIcon className="mr-1 h-3.5 w-3.5" />Generar ahora</Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
                  <span>{r.cutoffTime ? `Corte ${r.cutoffTime}${r.notifyName ? ` · avisa a ${r.notifyName.split(' ')[0]}` : ''}` : 'Sin hora de corte'}</span>
                  <span>{r.items.length} punto{r.items.length === 1 ? '' : 's'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RoutineEditor
        open={editor.open}
        routine={editor.routine}
        assignees={assignees}
        currentUserId={currentUserId}
        onClose={() => setEditor({ open: false, routine: null })}
        onSaved={() => { cargar(true); onChanged?.(); }}
      />
    </div>
  );
};

export default RoutinesView;
